import React, { useState } from 'react';
import { motion } from "framer-motion";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faTrash, faRotate } from '@fortawesome/free-solid-svg-icons';
import ProfileModals from '../ProfileModals';


const AccountActions = ({ 
  isLoading, 
  theme, 
  onSignOut, 
  onDeleteAccount,
  onResetData 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);

  const handleDeleteClick = () => {
    setIsModalOpen(true);
  };

  const handleConfirmDelete = () => {
    setIsModalOpen(false);
    onDeleteAccount();
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
  };

  const handleSignOutClick = () => {
    setIsSignOutModalOpen(true);
  };

  const handleConfirmSignOut = () => {
    setIsSignOutModalOpen(false);
    onSignOut();
  };

  const handleCancelSignOut = () => {
    setIsSignOutModalOpen(false);
  };

  const styles = {
    card: {
      background: theme?.background?.card || theme?.backgrounds?.card,
      borderRadius: '1.25rem',
      padding: '2rem',
      border: `1px solid ${theme?.borders?.primary}`,
      boxShadow: theme?.shadows?.base,
      marginBottom: '1.5rem',
    },
    button: {
      width: '100%',
      padding: '1rem 2rem',
      borderRadius: '0.75rem',
      fontSize: '1rem',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    },
    
    // Gray button for Sign Out
    grayButton: {
      background: theme?.primary?.gray?.[500],
      color: theme?.text?.primary,
      boxShadow: `0 4px 12px ${theme?.primary?.gray?.[200]}`,
      transition: 'all 0.2s ease',
    },
    buttonHover: {
      transform: 'scale(1.02)',
      transition: 'all 0.2s ease',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
    },
    
    // Yellow button for Reset All Data
    yellowButton: {
      background: theme?.primary?.yellow?.[500],
      color: theme?.text?.primary,
      boxShadow: `0 4px 12px ${theme?.primary?.yellow?.[200]}`,
    },
   
    
    // Red button for Delete Account
    redButton: {
      background: theme?.primary?.red?.[500],
      color: theme?.text?.primary,
      boxShadow: `0 4px 12px ${theme?.primary?.red?.[200]}`,
    },
    
    
    secondaryButton: {
      background: theme?.buttons?.secondary?.background || theme?.background?.card,
      color: theme?.buttons?.secondary?.text || theme?.text?.primary,
      border: `1px solid ${theme?.buttons?.secondary?.border || theme?.borders?.primary}`,
      boxShadow: theme?.buttons?.secondary?.shadow,
    },
    secondaryButtonHover: {
      background: theme?.buttons?.secondary?.backgroundHover || theme?.background?.cardHover,
      borderColor: theme?.buttons?.secondary?.borderHover || theme?.borders?.hover,
      boxShadow: theme?.buttons?.secondary?.shadowHover,
      transform: 'translateY(-2px)',
    },
    dangerButton: {
      background: theme?.primary?.red?.[500] || theme?.semantic?.error,
      color: 'white',
      boxShadow: `0 4px 12px ${(theme?.primary?.red?.[500] || theme?.semantic?.error) + '30'}`,
    },
    dangerButtonHover: {
      background: theme?.primary?.red?.[600] || theme?.semantic?.error,
      boxShadow: `0 6px 20px ${(theme?.primary?.red?.[500] || theme?.semantic?.error) + '40'}`,
      transform: 'translateY(-2px)',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme?.backgrounds?.modal || 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      background: theme?.background?.card || theme?.backgrounds?.card,
      borderRadius: '1.25rem',
      padding: '2rem',
      maxWidth: '500px',
      width: '90%',
      border: `1px solid ${theme?.borders?.primary}`,
      boxShadow: theme?.shadows?.xl,
    },
    modalTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      marginBottom: '1rem',
    },
    modalText: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
      marginBottom: '2rem',
      lineHeight: '1.6',
    },
    modalActions: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'flex-end',
    },
  };

  return (
    <>
      <motion.div
        style={styles.card}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: theme?.text?.primary,
            marginBottom: '0.5rem'
          }}>
            Account Actions
          </h2>
          <p style={{
            fontSize: '0.875rem',
            color: theme?.text?.secondary
          }}>
            Manage your account and session
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Sign Out Button - Gray */}
          <motion.button
            style={{
              ...styles.button,
              ...styles.grayButton
            }}
            whileHover={{
              scale: 1.02,
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            onClick={handleSignOutClick}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
            {isLoading ? 'Signing Out...' : 'Sign Out'}
          </motion.button>

          {/* Reset All Data Button - Yellow */}
          <motion.button
            style={{
              ...styles.button,
              ...styles.yellowButton
            }}
            whileHover={{
              scale: 1.02,
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            onClick={onResetData}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faRotate} />
            {isLoading ? 'Resetting...' : 'Reset All Data'}
          </motion.button>

          {/* Delete Account Button - Red */}
          <motion.button
            style={{
              ...styles.button,
              ...styles.redButton
            }}
            whileHover={{
              scale: 1.02,
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            onClick={handleDeleteClick}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faTrash} />
            Delete Account
          </motion.button>
        </div>
      </motion.div>

      {isModalOpen && (
        <motion.div
          style={styles.modal}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            style={styles.modalContent}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <h2 style={styles.modalTitle}>⚠️ Delete Account</h2>
            <p style={styles.modalText}>
              This action will permanently delete your account and all associated data. 
              This process is irreversible and cannot be undone. Are you absolutely sure 
              you want to proceed?
            </p>
            <div style={styles.modalActions}>
              <motion.button
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  width: 'auto',
                  padding: '0.75rem 1.5rem'
                }}
                whileHover={{
                  scale: 1.02,
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                onClick={handleCancelDelete}
              >
                Cancel
              </motion.button>
              <motion.button
                style={{
                  ...styles.button,
                  ...styles.redButton,
                  width: 'auto',
                  padding: '0.75rem 1.5rem'
                }}
                whileHover={{
                  scale: 1.02,
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                onClick={handleConfirmDelete}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Yes, Delete Account'}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {isSignOutModalOpen && (
        <ProfileModals.SignOutModal
          isOpen={isSignOutModalOpen}
          onClose={handleCancelSignOut}
          onConfirm={handleConfirmSignOut}
          theme={theme}
        />
      )}
    </>
  );
};

export default AccountActions;
